// src/components/cart/CartSuggestions.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = 'https://madadline.com/api';

/* ---------- Cache mémoire (évite de recharger le catalogue à chaque ouverture) ---------- */
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let productsCache = null;
let productsCacheAt = 0;
let productsPromise = null;

const loadProducts = () => {
  if (productsCache && Date.now() - productsCacheAt < CACHE_TTL) {
    return Promise.resolve(productsCache);
  }
  if (!productsPromise) {
    productsPromise = axios
      .get(`${API}/products`)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data?.data || data?.products || []);
        productsCache = Array.isArray(list) ? list : [];
        productsCacheAt = Date.now();
        return productsCache;
      })
      .catch(() => [])
      .finally(() => { productsPromise = null; });
  }
  return productsPromise;
};

/* ---------- Helpers (mêmes règles que le panier) ---------- */
const getEffectivePrice = (product) => {
  const original = Number(product?.prix ?? 0);
  let discount = Number(product?.discount ?? 0);
  const newPrice = product?.new_price != null ? Number(product.new_price) : null;
  if (discount > 0 && newPrice != null) return newPrice;
  if (discount > 0 && discount <= 1) discount *= 100;
  if (discount > 0 && original > 0) return Math.round(original * (1 - discount / 100));
  return original;
};

const getDiscountPercent = (product) => {
  const original = Number(product?.prix ?? 0);
  const price = getEffectivePrice(product);
  if (original > 0 && price < original) return Math.round((1 - price / original) * 100);
  return 0;
};

const getImageUrl = (image) => {
  if (!image || typeof image !== 'string') return '';
  const clean = image.trim();
  if (/^https?:\/\//i.test(clean)) return clean;
  const filename = clean.split('/').pop();
  if (!filename) return '';
  return `${API}/product-images/${encodeURIComponent(filename)}`;
};

const getStock = (p) => {
  if (Array.isArray(p?.variants) && p.variants.length > 0) {
    return p.variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0);
  }
  return p?.stock != null ? Number(p.stock) : null; // null = inconnu → on garde
};

const getCategoryIds = (p) =>
  Array.isArray(p?.categories)
    ? p.categories.map((c) => String(c?.id ?? c?.name ?? c))
    : [];

/* Nombre pseudo-aléatoire stable pour un produit donné et un tirage donné */
const pseudoRandom = (id, seed) => {
  const x = Math.sin((Number(id) || 0) * 12.9898 + seed * 78.233) * 43758.5453;
  return x - Math.floor(x);
};

/**
 * Suggestions de produits pour le panier.
 * - variant="drawer" : ligne horizontale défilante (panier latéral)
 * - variant="page"   : grille (page /cart)
 * - onNavigate       : appelé au clic (ex. fermer le drawer)
 * - enabled          : charge/recharge les suggestions quand true (ouverture du drawer)
 */
function CartSuggestions({
  cartItems = [],
  variant = 'drawer',
  limit,
  enabled = true,
  onNavigate,
}) {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [seed, setSeed] = useState(() => Math.random());

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setSeed(Math.random()); // nouveau tirage à chaque ouverture
    loadProducts().then((list) => { if (alive) setProducts(list); });
    return () => { alive = false; };
  }, [enabled]);

  const maxItems = limit || (variant === 'page' ? 4 : 6);

  const suggestions = useMemo(() => {
    if (products.length === 0) return [];

    const inCartIds = new Set(
      cartItems.map((i) => String(i.product?.id ?? i.product_id)).filter(Boolean)
    );

    const cartCategories = new Set();
    cartItems.forEach((i) => getCategoryIds(i.product).forEach((c) => cartCategories.add(c)));

    return products
      .filter((p) => p && p.id != null && !inCartIds.has(String(p.id)))
      .filter((p) => { const s = getStock(p); return s === null || s > 0; })
      .filter((p) => getImageUrl(p.image || p.image_url))
      .map((p) => {
        const overlap = getCategoryIds(p).filter((c) => cartCategories.has(c)).length;
        const promoBonus = getDiscountPercent(p) > 0 ? 1 : 0;
        return { p, score: overlap * 2 + promoBonus + pseudoRandom(p.id, seed) * 1.5 };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, maxItems)
      .map((x) => x.p);
  }, [products, cartItems, seed, maxItems]);

  if (suggestions.length === 0) return null;

  return (
    <section className={`cart-suggest cart-suggest--${variant}`}>
      <header className="cart-suggest__head">
        <h3 className="cart-suggest__title">
          {t('cart.suggestTitle', { defaultValue: 'Vous aimerez aussi' })}
        </h3>
        <p className="cart-suggest__sub">
          {t('cart.suggestSub', { defaultValue: 'Complétez votre commande avec nos coups de cœur' })}
        </p>
      </header>

      <div className="cart-suggest__list">
        {suggestions.map((p) => {
          const name = p.nom || p.name || '';
          const price = getEffectivePrice(p);
          const original = Number(p.prix || 0);
          const percent = getDiscountPercent(p);
          const img = getImageUrl(p.image || p.image_url);

          return (
            <Link
              key={p.id}
              to={`/product/${p.id}`}
              className="cart-suggest__card"
              onClick={() => { if (typeof onNavigate === 'function') onNavigate(); }}
            >
              <span className="cart-suggest__img">
                <img
                  src={img}
                  alt={name}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                />
                {percent > 0 && <span className="cart-suggest__badge">-{percent}%</span>}
              </span>

              <span className="cart-suggest__name">{name}</span>

              <span className="cart-suggest__price">
                <strong>{price.toFixed(0)} DH</strong>
                {percent > 0 && <s>{original.toFixed(0)} DH</s>}
              </span>

              <span className="cart-suggest__cta">
                {t('cart.suggestCta', { defaultValue: 'Découvrir' })} →
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export default CartSuggestions;